import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import safeStorage from '../../utils/safeStorage';

// API URL configuration
const API_URL = process.env.REACT_APP_API_URL || 
  (window.location.hostname === 'localhost' && window.location.port === '3002'
    ? 'http://localhost:3000/api'
    : '/api');

const api = axios.create({ baseURL: API_URL });

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = safeStorage.getItem('authToken');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

const styles = {
  container: {
    padding: '24px',
    maxWidth: '1400px',
    margin: '0 auto'
  },
  header: {
    marginBottom: '24px'
  },
  title: {
    fontSize: '1.75rem',
    fontWeight: '700',
    color: '#2d3748',
    marginBottom: '8px'
  },
  subtitle: {
    fontSize: '0.95rem',
    color: '#718096'
  },
  filterRow: {
    display: 'flex',
    gap: '16px',
    marginBottom: '24px',
    flexWrap: 'wrap',
    alignItems: 'center'
  },
  select: {
    padding: '10px 14px',
    fontSize: '14px',
    border: '1px solid #e2e8f0',
    borderRadius: '8px',
    minWidth: '200px',
    background: 'white'
  },
  mainContent: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '24px'
  },
  panel: {
    background: 'white',
    borderRadius: '8px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
    overflow: 'hidden'
  },
  panelHeader: {
    background: '#2563eb',
    color: 'white',
    padding: '16px 20px',
    fontWeight: '600',
    fontSize: '1rem'
  },
  panelContent: {
    padding: '16px',
    maxHeight: '600px',
    overflowY: 'auto'
  },
  treeNode: {
    marginBottom: '4px'
  },
  treeNodeHeader: {
    display: 'flex',
    alignItems: 'center',
    padding: '10px 12px',
    borderRadius: '8px',
    cursor: 'pointer',
    transition: 'all 0.2s',
    border: '2px solid transparent',
    background: '#f8fafc'
  },
  treeNodeHeaderHover: {
    background: '#edf2f7'
  },
  treeNodeHeaderDropTarget: {
    border: '2px dashed #2563eb',
    background: '#ebf4ff'
  },
  treeNodeHeaderDragging: {
    opacity: 0.5,
    background: '#fed7d7'
  },
  expandIcon: {
    width: '20px',
    height: '20px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: '8px',
    cursor: 'pointer',
    color: '#718096',
    fontSize: '12px'
  },
  ampIcon: {
    marginRight: '10px',
    fontSize: '16px'
  },
  ampInfo: {
    flex: 1
  },
  ampName: {
    fontWeight: '600',
    color: '#2d3748',
    fontSize: '0.9rem'
  },
  ampModel: {
    fontSize: '0.8rem',
    color: '#718096'
  },
  childCount: {
    fontSize: '0.75rem',
    background: '#e2e8f0',
    color: '#4a5568',
    padding: '2px 8px',
    borderRadius: '8px',
    marginLeft: '8px'
  },
  childrenContainer: {
    marginLeft: '28px',
    borderLeft: '2px solid #e2e8f0',
    paddingLeft: '12px'
  },
  dragHandle: {
    cursor: 'grab',
    padding: '4px',
    marginRight: '8px',
    color: '#a0aec0'
  },
  primaryAmpCard: {
    display: 'flex',
    alignItems: 'center',
    padding: '12px 14px',
    borderRadius: '8px',
    marginBottom: '8px',
    background: '#f8fafc',
    border: '2px solid transparent',
    cursor: 'grab',
    transition: 'all 0.2s'
  },
  primaryAmpCardDragging: {
    opacity: 0.5,
    background: '#fed7d7'
  },
  infoBox: {
    background: '#ebf8ff',
    border: '1px solid #90cdf4',
    borderRadius: '8px',
    padding: '12px 16px',
    marginBottom: '20px',
    display: 'flex',
    alignItems: 'flex-start',
    gap: '12px'
  },
  infoIcon: {
    color: '#3182ce',
    fontSize: '20px',
    flexShrink: 0
  },
  infoText: {
    fontSize: '0.9rem',
    color: '#2c5282',
    lineHeight: '1.5'
  },
  emptyState: {
    textAlign: 'center',
    padding: '40px 20px',
    color: '#718096'
  },
  loadingSpinner: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    padding: '40px'
  },
  successMessage: {
    background: '#c6f6d5',
    border: '1px solid #68d391',
    borderRadius: '8px',
    padding: '12px 16px',
    marginBottom: '16px',
    color: '#22543d',
    display: 'flex',
    alignItems: 'center',
    gap: '8px'
  },
  errorMessage: {
    background: '#fed7d7',
    border: '1px solid #fc8181',
    borderRadius: '8px',
    padding: '12px 16px',
    marginBottom: '16px',
    color: '#742a2a',
    display: 'flex',
    alignItems: 'center',
    gap: '8px'
  },
  button: {
    background: '#2563eb',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    padding: '8px 16px',
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'transform 0.2s'
  },
  buttonSecondary: {
    background: '#e2e8f0',
    color: '#4a5568'
  },
  promoteButton: {
    background: '#f6ad55',
    color: 'white',
    border: 'none',
    borderRadius: '6px',
    padding: '4px 10px',
    fontSize: '12px',
    fontWeight: '600',
    cursor: 'pointer',
    marginLeft: '8px'
  },
  deleteButton: {
    background: '#fc8181',
    color: 'white',
    border: 'none',
    borderRadius: '6px',
    padding: '4px 10px',
    fontSize: '12px',
    fontWeight: '600',
    cursor: 'pointer',
    marginLeft: '8px'
  },
  searchInput: {
    padding: '10px 14px',
    fontSize: '14px',
    border: '1px solid #e2e8f0',
    borderRadius: '8px',
    minWidth: '250px',
    background: 'white'
  },
  crossManufacturerBadge: {
    background: '#f59e0b',
    color: 'white',
    padding: '4px 12px',
    borderRadius: '8px',
    fontSize: '12px',
    fontWeight: '600',
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px'
  },
  manufacturerSelectorRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '12px 16px',
    borderBottom: '1px solid #e2e8f0',
    background: '#f8fafc'
  }
};

const TreeNode = ({ amp, children, onDrop, onExpand, expanded, onPromote, onDelete, draggedAmp, selectedAmpId, onSelect, onDragStart, onDragEnd }) => {
  const [isHovered, setIsHovered] = useState(false);
  const [isDropTarget, setIsDropTarget] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const nodeRef = useRef(null);
  const isSelected = selectedAmpId === amp.amplifier_id;

  const handleDragStart = (e) => {
    setIsDragging(true);
    if (onDragStart) {
      onDragStart(e, amp);
    }
  };

  const handleDragEnd = (e) => {
    setIsDragging(false);
    if (onDragEnd) {
      onDragEnd(e);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (draggedAmp && draggedAmp.amplifier_id !== amp.amplifier_id) {
      setIsDropTarget(true);
    }
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDropTarget(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDropTarget(false);
    if (draggedAmp && draggedAmp.amplifier_id !== amp.amplifier_id) {
      onDrop(draggedAmp, amp);
    }
  };

  const hasChildren = children && children.length > 0;
  const childCount = amp.child_count || (children ? children.length : 0);

  return (
    <div style={styles.treeNode}>
      <div
        ref={nodeRef}
        draggable
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
        style={{
          ...styles.treeNodeHeader,
          ...(isSelected ? { background: '#e0e7ff', borderLeft: '4px solid #2563eb' } : {}),
          ...(isHovered && !isDropTarget && !isSelected ? styles.treeNodeHeaderHover : {}),
          ...(isDropTarget ? styles.treeNodeHeaderDropTarget : {}),
          ...(isDragging ? styles.treeNodeHeaderDragging : {})
        }}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        <div
          style={styles.expandIcon}
          onClick={() => hasChildren && onExpand(amp.amplifier_id)}
        >
          {hasChildren ? (expanded ? '▼' : '▶') : ''}
        </div>
        
        <div 
          style={{ ...styles.ampInfo, cursor: 'pointer' }}
          onClick={(e) => {
            e.stopPropagation();
            onSelect(amp.amplifier_id);
            window.open(amp.manufacturer_slug && amp.slug ? `/amps/${amp.manufacturer_slug}/${amp.slug}` : `/amplifier-components/${amp.amplifier_id}`, '_blank');
          }}
          title="Click to open amplifier details in new tab"
        >
          <div style={styles.ampName}>
            {amp.name || amp.model || 'Unnamed Amp'}
            {childCount > 0 && (
              <span style={styles.childCount}>{childCount} child{childCount !== 1 ? 'ren' : ''}</span>
            )}
          </div>
          <div style={styles.ampModel}>
            {amp.manufacturer_name} • {amp.model || 'No model'}
            {amp.start_year && ` • ${amp.start_year}${amp.end_year && amp.end_year !== amp.start_year ? `-${amp.end_year}` : ''}`}
          </div>
        </div>
        {!amp.is_parent && (
          <button
            style={styles.promoteButton}
            onClick={(e) => {
              e.stopPropagation();
              onPromote(amp);
            }}
            title="Promote to primary amplifier"
          >
            Promote
          </button>
        )}
        <button
          style={styles.deleteButton}
          onClick={(e) => {
            e.stopPropagation();
            onDelete(amp);
          }}
          title="Delete amplifier"
        >
          Delete
        </button>
      </div>
      {expanded && hasChildren && (
        <div style={styles.childrenContainer}>
          {children.map(child => (
            <TreeNode
              key={child.amplifier_id}
              amp={child}
              children={[]}
              onDrop={onDrop}
              onExpand={onExpand}
              expanded={false}
              onPromote={onPromote}
              onDelete={onDelete}
              draggedAmp={draggedAmp}
              selectedAmpId={selectedAmpId}
              onSelect={onSelect}
              onDragStart={onDragStart}
              onDragEnd={onDragEnd}
            />
          ))}
        </div>
      )}
    </div>
  );
};

const DraggableAmpCard = ({ amp, onDragStart, onDragEnd, isDragging, onDrop, onDelete, draggedAmp, selectedAmpId, onSelect }) => {
  const [isDropTarget, setIsDropTarget] = useState(false);
  const isSelected = selectedAmpId === amp.amplifier_id;

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (draggedAmp && draggedAmp.amplifier_id !== amp.amplifier_id) {
      setIsDropTarget(true);
    }
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDropTarget(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDropTarget(false);
    if (draggedAmp && draggedAmp.amplifier_id !== amp.amplifier_id && onDrop) {
      onDrop(draggedAmp, amp);
    }
  };

  return (
    <div
      draggable
      onDragStart={(e) => onDragStart(e, amp)}
      onDragEnd={onDragEnd}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      style={{
        ...styles.primaryAmpCard,
        ...(isSelected ? { background: '#e0e7ff', borderLeft: '4px solid #2563eb' } : {}),
        ...(isDragging ? styles.primaryAmpCardDragging : {}),
        ...(isDropTarget ? { border: '2px dashed #2563eb', background: '#ebf4ff' } : {})
      }}
    >
      <span style={styles.dragHandle}>⋮⋮</span>
      
      <div 
        style={{ ...styles.ampInfo, cursor: 'pointer' }}
        onClick={(e) => {
          e.stopPropagation();
          onSelect(amp.amplifier_id);
          window.open(amp.manufacturer_slug && amp.slug ? `/amps/${amp.manufacturer_slug}/${amp.slug}` : `/amplifier-components/${amp.amplifier_id}`, '_blank');
        }}
        title="Click to open amplifier details in new tab"
      >
        <div style={styles.ampName}>{amp.name || amp.model || 'Unnamed Amp'}</div>
        <div style={styles.ampModel}>
          {amp.manufacturer_name} • {amp.model || 'No model'}
          {amp.start_year && ` • ${amp.start_year}${amp.end_year && amp.end_year !== amp.start_year ? `-${amp.end_year}` : ''}`}
        </div>
      </div>
      <button
        style={styles.deleteButton}
        onClick={(e) => {
          e.stopPropagation();
          onDelete(amp);
        }}
        title="Delete amplifier"
      >
        Delete
      </button>
    </div>
  );
};

const AmplifierHierarchyScreen = () => {
  const { isAdmin } = useAuth();
  const [manufacturers, setManufacturers] = useState([]);
  const [selectedManufacturer, setSelectedManufacturer] = useState('');
  const [availableManufacturerId, setAvailableManufacturerId] = useState('');
  const [parentAmps, setParentAmps] = useState([]);
  const [orphanedPrimaries, setOrphanedPrimaries] = useState([]);
  const [childrenMap, setChildrenMap] = useState({});
  const [expandedNodes, setExpandedNodes] = useState(new Set());
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState(null);
  const [draggedAmp, setDraggedAmp] = useState(null);
  const [leftFilter, setLeftFilter] = useState('');
  const [rightFilter, setRightFilter] = useState('');
  const [selectedAmpId, setSelectedAmpId] = useState(null);
  const [manufacturerSearch, setManufacturerSearch] = useState('');
  const [rightPanelDropTarget, setRightPanelDropTarget] = useState(false);
  
  // Refs for scroll position preservation
  const leftPanelRef = useRef(null);
  const rightPanelRef = useRef(null);
  const scrollPositionsRef = useRef({ left: 0, right: 0 });

  // Fetch manufacturers on mount
  useEffect(() => {
    fetchManufacturers();
  }, []);

  // Sync availableManufacturerId with selectedManufacturer initially
  useEffect(() => {
    if (selectedManufacturer && !availableManufacturerId) {
      setAvailableManufacturerId(selectedManufacturer);
    }
  }, [selectedManufacturer, availableManufacturerId]);

  // Fetch amplifiers when manufacturer changes
  useEffect(() => {
    if (selectedManufacturer) {
      fetchAmplifiers();
    } else {
      setParentAmps([]);
      setOrphanedPrimaries([]);
      setChildrenMap({});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedManufacturer]);

  // Fetch available amps when availableManufacturerId changes
  useEffect(() => {
    if (availableManufacturerId && availableManufacturerId !== selectedManufacturer) {
      fetchAvailableAmps();
    } else if (availableManufacturerId === selectedManufacturer) {
      // If they match, available amps come from main fetch
      // No need for separate fetch
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [availableManufacturerId]);

  const fetchManufacturers = async () => {
    try {
      const response = await api.get('/manufacturers');
      const sortedManufacturers = (response.data || []).sort((a, b) => 
        (a.manufacturer_name || '').localeCompare(b.manufacturer_name || '')
      );
      setManufacturers(sortedManufacturers);
    } catch (error) {
      console.error('Error fetching manufacturers:', error);
    }
  };

  // Helper function to fetch all pages of amplifiers for a manufacturer
  const fetchAllAmplifiersForManufacturer = async (manufacturerId) => {
    const allAmps = [];
    let page = 1;
    let hasMore = true;
    const pageSize = 500; // Use max page size for efficiency
    
    while (hasMore) {
      const response = await api.get('/amplifiers', {
        params: { manufacturer_id: manufacturerId, page, pageSize }
      });
      
      const data = response.data?.data || [];
      const pagination = response.data?.pagination;
      
      allAmps.push(...data);
      
      // Check if there are more pages
      hasMore = pagination?.hasNext || false;
      page++;
      
      // Safety limit to prevent infinite loops
      if (page > 100) break;
    }
    
    return allAmps;
  };

  const fetchAvailableAmps = async () => {
    try {
      const allAmps = await fetchAllAmplifiersForManufacturer(availableManufacturerId);
      const childMap = {};
      
      // Build children map
      allAmps.forEach(amp => {
        if (amp.parent_id) {
          if (!childMap[amp.parent_id]) {
            childMap[amp.parent_id] = [];
          }
          childMap[amp.parent_id].push(amp);
        }
      });
      
      // Get only standalone primaries (no children, not a child)
      const primaries = allAmps.filter(amp => 
        !amp.parent_id && !childMap[amp.amplifier_id]
      );
      
      primaries.sort((a, b) => (a.name || a.model || '').localeCompare(b.name || b.model || ''));
      setOrphanedPrimaries(primaries);
      
    } catch (error) {
      console.error('Error fetching available amplifiers:', error);
      setMessage({ type: 'error', text: 'Failed to load available amplifiers' });
    }
  };

  const fetchAmplifiers = async () => {
    setLoading(true);
    try {
      // Fetch all amplifiers for the selected manufacturer (all pages)
      const allAmps = await fetchAllAmplifiersForManufacturer(selectedManufacturer);
      
      // Separate parent amps from child/draggable amps
      const parents = [];
      const primaries = [];
      const childMap = {};
      
      // First pass: build children map
      allAmps.forEach(amp => {
        if (amp.parent_id) {
          // This is a child amp
          if (!childMap[amp.parent_id]) {
            childMap[amp.parent_id] = [];
          }
          childMap[amp.parent_id].push(amp);
        }
      });
      
      // Second pass: categorize
      // Left panel: All top-level amps that are marked as is_parent=true (potential drop targets)
      // Right panel: All top-level amps that don't have children yet (can be dragged to become children)
      allAmps.forEach(amp => {
        if (!amp.parent_id) {
          // This is a top-level amp
          const hasChildren = childMap[amp.amplifier_id] && childMap[amp.amplifier_id].length > 0;
          
          if (amp.is_parent) {
            // It's marked as a parent - show in left panel as drop target
            const childCount = childMap[amp.amplifier_id]?.length || 0;
            parents.push({ ...amp, child_count: childCount });
          }
          
          if (!hasChildren) {
            // No children yet - show in right panel as draggable (can become a child)
            primaries.push(amp);
          }
        }
      });
      
      // Sort alphabetically
      parents.sort((a, b) => (a.name || a.model || '').localeCompare(b.name || b.model || ''));
      primaries.sort((a, b) => (a.name || a.model || '').localeCompare(b.name || b.model || ''));
      
      setParentAmps(parents);
      setChildrenMap(childMap);
      
      // Only set orphanedPrimaries if we're viewing the same manufacturer
      // Otherwise, they'll come from fetchAvailableAmps
      if (availableManufacturerId === selectedManufacturer || !availableManufacturerId) {
        setOrphanedPrimaries(primaries);
      }
      
    } catch (error) {
      console.error('Error fetching amplifiers:', error);
      setMessage({ type: 'error', text: 'Failed to load amplifiers' });
    } finally {
      setLoading(false);
    }
  };

  const handleExpand = (ampId) => {
    setExpandedNodes(prev => {
      const newSet = new Set(prev);
      if (newSet.has(ampId)) {
        newSet.delete(ampId);
      } else {
        newSet.add(ampId);
      }
      return newSet;
    });
  };

  const handleDragStart = (e, amp) => {
    setDraggedAmp(amp);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', amp.amplifier_id.toString());
  };

  const handleDragEnd = () => {
    setDraggedAmp(null);
  };

  const handleDrop = async (childAmp, parentAmp) => {
    if (!childAmp || !parentAmp) return;
    if (childAmp.amplifier_id === parentAmp.amplifier_id) return;
    
    // Prevent making a parent a child of one of its own children
    if (childrenMap[childAmp.amplifier_id]?.some(c => c.amplifier_id === parentAmp.amplifier_id)) {
      setMessage({ type: 'error', text: 'Cannot make an amp a child of one of its own children' });
      return;
    }
    
    // Check if we're moving between manufacturers
    const isManufacturerMove = availableManufacturerId !== selectedManufacturer;
    
    // Save scroll positions before refresh
    scrollPositionsRef.current = {
      left: leftPanelRef.current?.scrollTop || 0,
      right: rightPanelRef.current?.scrollTop || 0
    };

    try {
      setLoading(true);
      
      // Update the child amp to have the new parent AND potentially new manufacturer
      const updateData = {
        ...childAmp,
        is_parent: false,
        parent_id: parentAmp.amplifier_id
      };
      
      // If moving between manufacturers, update the manufacturer_id
      if (isManufacturerMove) {
        updateData.manufacturer_id = parentAmp.manufacturer_id;
      }
      
      await api.put(`/amplifiers/${childAmp.amplifier_id}`, updateData);
      
      // If the parent wasn't already a parent, update it
      if (!parentAmp.is_parent) {
        await api.put(`/amplifiers/${parentAmp.amplifier_id}`, {
          ...parentAmp,
          is_parent: true,
          parent_id: null
        });
      }
      
      const successMessage = isManufacturerMove
        ? `"${childAmp.name || childAmp.model}" moved to ${manufacturers.find(m => m.manufacturer_id === selectedManufacturer)?.manufacturer_name} and made a child of "${parentAmp.name || parentAmp.model}"`
        : `"${childAmp.name || childAmp.model}" is now a child of "${parentAmp.name || parentAmp.model}"`;
      
      setMessage({ 
        type: 'success', 
        text: successMessage
      });
      
      // Refresh both panels
      await fetchAmplifiers();
      if (availableManufacturerId !== selectedManufacturer) {
        await fetchAvailableAmps();
      }
      
      // Restore scroll positions after a brief delay to allow DOM to update
      setTimeout(() => {
        if (leftPanelRef.current) {
          leftPanelRef.current.scrollTop = scrollPositionsRef.current.left;
        }
        if (rightPanelRef.current) {
          rightPanelRef.current.scrollTop = scrollPositionsRef.current.right;
        }
      }, 50);
      
      // Expand the parent to show the new child
      setExpandedNodes(prev => new Set(prev).add(parentAmp.amplifier_id));
      
    } catch (error) {
      console.error('Error updating amplifier hierarchy:', error);
      setMessage({ type: 'error', text: error.response?.data?.error || 'Failed to update hierarchy' });
    } finally {
      setLoading(false);
      setDraggedAmp(null);
    }
  };

  const handleTransferToManufacturer = async (amp) => {
    if (!amp || !availableManufacturerId) return;
    if (amp.manufacturer_id === availableManufacturerId) {
      setMessage({ type: 'error', text: 'Amp is already in this manufacturer' });
      return;
    }
    
    // Save scroll positions before refresh
    scrollPositionsRef.current = {
      left: leftPanelRef.current?.scrollTop || 0,
      right: rightPanelRef.current?.scrollTop || 0
    };

    try {
      setLoading(true);
      
      const targetManufacturer = manufacturers.find(m => m.manufacturer_id === availableManufacturerId);
      
      // Transfer amp to new manufacturer and make it standalone
      await api.put(`/amplifiers/${amp.amplifier_id}`, {
        ...amp,
        manufacturer_id: availableManufacturerId,
        is_parent: true,
        parent_id: null
      });
      
      setMessage({ 
        type: 'success', 
        text: `"${amp.name || amp.model}" transferred to ${targetManufacturer?.manufacturer_name}`
      });
      
      // Refresh both panels
      await fetchAmplifiers();
      if (availableManufacturerId !== selectedManufacturer) {
        await fetchAvailableAmps();
      }
      
      // Restore scroll positions after a brief delay to allow DOM to update
      setTimeout(() => {
        if (leftPanelRef.current) {
          leftPanelRef.current.scrollTop = scrollPositionsRef.current.left;
        }
        if (rightPanelRef.current) {
          rightPanelRef.current.scrollTop = scrollPositionsRef.current.right;
        }
      }, 50);
      
    } catch (error) {
      console.error('Error transferring amplifier:', error);
      setMessage({ type: 'error', text: error.response?.data?.error || 'Failed to transfer amplifier' });
    } finally {
      setLoading(false);
      setDraggedAmp(null);
    }
  };

  const handleDelete = async (amp) => {
    const childCount = childrenMap[amp.amplifier_id]?.length || 0;
    const confirmMessage = childCount > 0
      ? `Delete "${amp.name || amp.model}"?\n\nThis amp has ${childCount} child${childCount !== 1 ? 'ren' : ''} that will also be deleted. This action cannot be undone.`
      : `Delete "${amp.name || amp.model}"?\n\nThis action cannot be undone.`;
    
    if (!window.confirm(confirmMessage)) {
      return;
    }
    
    // Save scroll positions before refresh
    scrollPositionsRef.current = {
      left: leftPanelRef.current?.scrollTop || 0,
      right: rightPanelRef.current?.scrollTop || 0
    };

    try {
      setLoading(true);
      
      await api.delete(`/amplifiers/${amp.amplifier_id}`);
      
      setMessage({ 
        type: 'success', 
        text: `"${amp.name || amp.model}" has been deleted`
      });
      
      await fetchAmplifiers();
      
      // Restore scroll positions after a brief delay to allow DOM to update
      setTimeout(() => {
        if (leftPanelRef.current) {
          leftPanelRef.current.scrollTop = scrollPositionsRef.current.left;
        }
        if (rightPanelRef.current) {
          rightPanelRef.current.scrollTop = scrollPositionsRef.current.right;
        }
      }, 50);
      
    } catch (error) {
      console.error('Error deleting amplifier:', error);
      setMessage({ type: 'error', text: error.response?.data?.error || 'Failed to delete amplifier' });
    } finally {
      setLoading(false);
    }
  };

  const handlePromote = async (amp) => {
    if (!window.confirm(`Promote "${amp.name || amp.model}" to a primary amplifier?\n\nThis will remove it from its parent.`)) {
      return;
    }
    
    // Save scroll positions before refresh
    scrollPositionsRef.current = {
      left: leftPanelRef.current?.scrollTop || 0,
      right: rightPanelRef.current?.scrollTop || 0
    };

    try {
      setLoading(true);
      
      await api.put(`/amplifiers/${amp.amplifier_id}`, {
        ...amp,
        is_parent: true,
        parent_id: null
      });
      
      setMessage({ 
        type: 'success', 
        text: `"${amp.name || amp.model}" has been promoted to a primary amplifier`
      });
      
      await fetchAmplifiers();
      
      // Restore scroll positions after a brief delay to allow DOM to update
      setTimeout(() => {
        if (leftPanelRef.current) {
          leftPanelRef.current.scrollTop = scrollPositionsRef.current.left;
        }
        if (rightPanelRef.current) {
          rightPanelRef.current.scrollTop = scrollPositionsRef.current.right;
        }
      }, 50);
      
    } catch (error) {
      console.error('Error promoting amplifier:', error);
      setMessage({ type: 'error', text: error.response?.data?.error || 'Failed to promote amplifier' });
    } finally {
      setLoading(false);
    }
  };

  // Filter orphaned primaries by right panel filter
  const filteredOrphanedPrimaries = orphanedPrimaries.filter(amp => {
    if (!rightFilter) return true;
    const search = rightFilter.toLowerCase();
    return (
      (amp.name && amp.name.toLowerCase().includes(search)) ||
      (amp.model && amp.model.toLowerCase().includes(search)) ||
      (amp.manufacturer_name && amp.manufacturer_name.toLowerCase().includes(search))
    );
  });

  // Filter parent amps by left panel filter
  const filteredParentAmps = parentAmps.filter(amp => {
    if (!leftFilter) return true;
    const search = leftFilter.toLowerCase();
    return (
      (amp.name && amp.name.toLowerCase().includes(search)) ||
      (amp.model && amp.model.toLowerCase().includes(search)) ||
      (amp.manufacturer_name && amp.manufacturer_name.toLowerCase().includes(search))
    );
  });

  if (!isAdmin) {
    return (
      <div style={styles.container}>
        <div style={styles.errorMessage}>
          <span>⚠️</span>
          <span>This screen is only accessible to administrators.</span>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <h1 style={styles.title}>Amplifier Hierarchy Manager</h1>
        <p style={styles.subtitle}>
          Organize amplifiers by dragging primary amps onto parent amps to make them children
        </p>
      </div>

      <div style={styles.infoBox}>
        <span style={styles.infoIcon}>ℹ️</span>
        <div style={styles.infoText}>
          <strong>How to use:</strong> Select a manufacturer, then drag amplifiers from the "Primary Amplifiers" 
          panel (right) onto amplifiers in the "Hierarchy" panel (left) to make them children. 
          You can also drag onto other primary amps in the left panel to create a new parent-child relationship.
          Click "Promote" on any child to make it a standalone primary amplifier again.
        </div>
      </div>

      {message && (
        <div style={message.type === 'success' ? styles.successMessage : styles.errorMessage}>
          <span>{message.type === 'success' ? '✓' : '⚠️'}</span>
          <span>{message.text}</span>
          <button
            onClick={() => setMessage(null)}
            style={{ marginLeft: 'auto', background: 'none', border: 'none', cursor: 'pointer' }}
          >
            ✕
          </button>
        </div>
      )}

      <div style={styles.filterRow}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          <label style={{ fontSize: '14px', fontWeight: '600', color: '#4a5568' }}>Parent Amps Manufacturer:</label>
          <input
            type="text"
            placeholder="Search manufacturers..."
            style={styles.searchInput}
            value={manufacturerSearch}
            onChange={(e) => setManufacturerSearch(e.target.value)}
          />
          <select
            style={styles.select}
            value={selectedManufacturer}
            onChange={(e) => {
              setSelectedManufacturer(e.target.value);
              // Sync available manufacturer unless user has explicitly set it different
              if (!availableManufacturerId || availableManufacturerId === selectedManufacturer) {
                setAvailableManufacturerId(e.target.value);
              }
            }}
            size="8"
          >
            <option value="">Select a Manufacturer</option>
            {manufacturers
              .filter(m => 
                !manufacturerSearch || 
                (m.manufacturer_name || '').toLowerCase().includes(manufacturerSearch.toLowerCase())
              )
              .map(m => (
                <option key={m.manufacturer_id} value={m.manufacturer_id}>
                  {m.manufacturer_name}
                </option>
              ))}
          </select>
        </div>
      </div>

      {loading && (
        <div style={styles.loadingSpinner}>
          <span>Loading amplifiers...</span>
        </div>
      )}

      {!loading && selectedManufacturer && (
        <div style={styles.mainContent}>
          {/* Left panel: Hierarchy tree */}
          <div style={styles.panel}>
            <div style={styles.panelHeader}>
              Parent Amplifiers ({filteredParentAmps.length}/{parentAmps.length})
            </div>
            <div style={{ padding: '12px 16px', borderBottom: '1px solid #e2e8f0' }}>
              <input
                type="text"
                style={{ ...styles.searchInput, width: '100%', minWidth: 'unset' }}
                placeholder="Filter hierarchy (e.g., JCM 800)..."
                value={leftFilter}
                onChange={(e) => setLeftFilter(e.target.value)}
              />
            </div>
            <div ref={leftPanelRef} style={styles.panelContent}>
              {filteredParentAmps.length === 0 ? (
                <div style={styles.emptyState}>
                  <p>No parent amplifiers found.</p>
                  <p style={{ fontSize: '0.85rem', marginTop: '8px' }}>
                    Parent amplifiers are amps marked with is_parent=true in the database.
                  </p>
                </div>
              ) : (
                filteredParentAmps.map(amp => (
                  <TreeNode
                    key={amp.amplifier_id}
                    amp={amp}
                    children={childrenMap[amp.amplifier_id] || []}
                    onDrop={handleDrop}
                    onExpand={handleExpand}
                    expanded={expandedNodes.has(amp.amplifier_id)}
                    onPromote={handlePromote}
                    onDelete={handleDelete}
                    draggedAmp={draggedAmp}
                    selectedAmpId={selectedAmpId}
                    onSelect={setSelectedAmpId}
                    onDragStart={handleDragStart}
                    onDragEnd={handleDragEnd}
                  />
                ))
              )}
            </div>
          </div>

          {/* Right panel: Non-parent amps (can be dragged to become children) */}
          <div style={styles.panel}>
            <div style={styles.panelHeader}>
              Available Amplifiers - {filteredOrphanedPrimaries.length}/{orphanedPrimaries.length}
            </div>
            <div style={styles.manufacturerSelectorRow}>
              <select
                style={{ ...styles.select, minWidth: '180px', flex: 1 }}
                value={availableManufacturerId}
                onChange={(e) => setAvailableManufacturerId(e.target.value)}
              >
                <option value="">Select Manufacturer</option>
                {manufacturers.map(m => (
                  <option key={m.manufacturer_id} value={m.manufacturer_id}>
                    {m.manufacturer_name}
                  </option>
                ))}
              </select>
              {availableManufacturerId && availableManufacturerId !== selectedManufacturer && (
                <div style={styles.crossManufacturerBadge}>
                  <span>⇄</span>
                  <span>Moving between manufacturers</span>
                </div>
              )}
            </div>
            <div style={{ padding: '12px 16px', borderBottom: '1px solid #e2e8f0' }}>
              <input
                type="text"
                style={{ ...styles.searchInput, width: '100%', minWidth: 'unset' }}
                placeholder="Filter primaries (e.g., JCM 800)..."
                value={rightFilter}
                onChange={(e) => setRightFilter(e.target.value)}
              />
            </div>
            <div 
              ref={rightPanelRef} 
              style={{
                ...styles.panelContent,
                ...(rightPanelDropTarget ? { 
                  background: '#ebf4ff',
                  border: '2px dashed #2563eb'
                } : {})
              }}
              onDragOver={(e) => {
                e.preventDefault();
                e.stopPropagation();
                // Only allow drops from parent panel (different manufacturer)
                if (draggedAmp && availableManufacturerId && draggedAmp.manufacturer_id !== availableManufacturerId) {
                  setRightPanelDropTarget(true);
                }
              }}
              onDragLeave={(e) => {
                e.preventDefault();
                setRightPanelDropTarget(false);
              }}
              onDrop={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setRightPanelDropTarget(false);
                if (draggedAmp && availableManufacturerId && draggedAmp.manufacturer_id !== availableManufacturerId) {
                  handleTransferToManufacturer(draggedAmp);
                }
              }}
            >
              {filteredOrphanedPrimaries.length === 0 ? (
                <div style={styles.emptyState}>
                  <p>No standalone primary amplifiers.</p>
                  <p style={{ fontSize: '0.85rem', marginTop: '8px' }}>
                    All amplifiers either have children or are children themselves.
                  </p>
                </div>
              ) : (
                filteredOrphanedPrimaries.map(amp => (
                  <DraggableAmpCard
                    key={amp.amplifier_id}
                    amp={amp}
                    onDragStart={handleDragStart}
                    onDragEnd={handleDragEnd}
                    isDragging={draggedAmp?.amplifier_id === amp.amplifier_id}
                    onDrop={handleDrop}
                    onDelete={handleDelete}
                    draggedAmp={draggedAmp}
                    selectedAmpId={selectedAmpId}
                    onSelect={setSelectedAmpId}
                  />
                ))
              )}
            </div>
          </div>
        </div>
      )}

      {!loading && !selectedManufacturer && (
        <div style={{ ...styles.panel, padding: '40px', textAlign: 'center' }}>
          <p style={{ color: '#718096', fontSize: '1rem' }}>
            Select a manufacturer to view and organize amplifier hierarchy
          </p>
        </div>
      )}
    </div>
  );
};

export default AmplifierHierarchyScreen;
